import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useFetcher } from 'react-router'
import { ServerOnly } from 'remix-utils/server-only'
import { useOptimisticConsentState } from '#app/routes/resources/consent.tsx'
import {
	OPEN_CONSENT_SETTINGS_EVENT,
	openConsentSettings,
} from '#app/utils/consent-settings.ts'
import {
	analyticsAllowed,
	type Consent,
	type ConsentMode,
} from '#app/utils/consent.ts'
import { formatDate, useLocale } from '#app/utils/intl.ts'
import { useRequestInfo } from '#app/utils/request-info.ts'
import { Button } from './ui/button.tsx'
import { Switch } from './ui/switch.tsx'

/**
 * The Analytics Consent card (ADR-075). App-level chrome like
 * `ImpersonationBanner` — not a `ui/*` primitive, so no design-sync lockstep.
 * Fixed bottom-right, a single `role="status"` region that never steals focus on
 * mount and leaves the page usable behind it (no scrim, no focus trap). A choice
 * posts to `/resources/consent`, which writes the pref cookie and — via
 * `useOptimisticConsentState` feeding the root `<Matomo/>` gate — boots or stops
 * the tracker before the network settles.
 *
 * One component, three faces (EPT-144):
 * - **gated** (`CONSENT_MODE=gated`, default) — the opt-in disclosure with
 *   Accept / Decline, shown once until a choice is recorded.
 * - **cookieless** (`CONSENT_MODE=cookieless`) — the informational notice with a
 *   single "Got it" and an opt-out link; Matomo already runs cookielessly.
 * - **settings** — re-opened by the footer "Privacy settings" link (or the
 *   cookieless opt-out link) to revisit the choice on a `Switch`. Both postures
 *   always disclose, so the obligation is met either way.
 */
const cardBase =
	'bg-card text-card-foreground border-border shadow-overlay fixed right-4 bottom-4 left-4 z-50 flex flex-col gap-2.5 rounded-lg border p-4 sm:left-auto'

export function ConsentBanner({
	consent,
	mode,
}: {
	consent: Consent
	mode: ConsentMode
}) {
	const [settingsOpen, setSettingsOpen] = useState(false)
	const titleRef = useRef<HTMLParagraphElement>(null)

	// The footer "Privacy settings" link and the cookieless opt-out link live
	// elsewhere in the tree; they ask this app-level card to open via a window
	// event (user-initiated re-open only).
	useEffect(() => {
		function open() {
			setSettingsOpen(true)
		}
		window.addEventListener(OPEN_CONSENT_SETTINGS_EVENT, open)
		return () => window.removeEventListener(OPEN_CONSENT_SETTINGS_EVENT, open)
	}, [])

	// Move focus to the card title on re-open. `settingsOpen` only ever flips true
	// after a user action, so this never steals focus on mount.
	useEffect(() => {
		if (settingsOpen) titleRef.current?.focus()
	}, [settingsOpen])

	const resolvedState = useOptimisticConsentState() ?? consent.state

	if (settingsOpen) {
		return (
			<SettingsCard
				titleRef={titleRef}
				consent={consent}
				allowed={analyticsAllowed(resolvedState, mode)}
				onDone={() => setSettingsOpen(false)}
			/>
		)
	}

	// The one-time disclosure only shows before a choice is recorded; afterwards
	// the footer link is the re-entry point.
	if (resolvedState !== 'unset') return null

	return mode === 'cookieless' ? <CookielessNotice /> : <GatedBanner />
}

/** The opt-in disclosure (gated posture): Accept / Decline. */
function GatedBanner() {
	const { t } = useTranslation('common')
	const fetcher = useFetcher()
	const requestInfo = useRequestInfo()

	return (
		<div role="status" aria-label={t('consent.label')} className={`${cardBase} sm:w-80`}>
			<p className="text-body-sm font-semibold">{t('consent.title')}</p>
			<p className="text-body-2xs text-muted-foreground">
				{t('consent.body')}{' '}
				<Link to="/datenschutz" className="text-brand underline">
					{t('consent.details')}
				</Link>
			</p>
			<fetcher.Form method="POST" action="/resources/consent" className="flex gap-2">
				<ServerOnly>
					{() => (
						<input type="hidden" name="redirectTo" value={requestInfo.path} />
					)}
				</ServerOnly>
				<Button type="submit" name="intent" value="accept" size="sm">
					{t('consent.accept')}
				</Button>
				<Button
					type="submit"
					name="intent"
					value="decline"
					size="sm"
					variant="outline"
				>
					{t('consent.decline')}
				</Button>
			</fetcher.Form>
		</div>
	)
}

/**
 * The informational notice (cookieless posture): Matomo already runs cookielessly,
 * so a single "Got it" acknowledges it and an opt-out link opens the settings card.
 */
function CookielessNotice() {
	const { t } = useTranslation('common')
	const fetcher = useFetcher()
	const requestInfo = useRequestInfo()

	return (
		<div
			role="status"
			aria-label={t('consent.notice.label')}
			className={`${cardBase} sm:w-80`}
		>
			<p className="text-body-sm font-semibold">{t('consent.notice.title')}</p>
			<p className="text-body-2xs text-muted-foreground">
				{t('consent.notice.body')}{' '}
				<button
					type="button"
					onClick={openConsentSettings}
					className="text-brand underline"
				>
					{t('consent.notice.optOut')}
				</button>
			</p>
			<fetcher.Form method="POST" action="/resources/consent" className="flex gap-2">
				<ServerOnly>
					{() => (
						<input type="hidden" name="redirectTo" value={requestInfo.path} />
					)}
				</ServerOnly>
				<Button type="submit" name="intent" value="dismiss" size="sm">
					{t('consent.notice.gotIt')}
				</Button>
			</fetcher.Form>
		</div>
	)
}

/**
 * The re-openable settings card: shows the recorded choice + date and a `Switch`
 * reflecting whether analytics are currently allowed. Toggling posts accept/decline
 * to persist the pref cookie and boot/stop Matomo immediately; "Done" only dismisses.
 */
function SettingsCard({
	titleRef,
	consent,
	allowed,
	onDone,
}: {
	titleRef: React.RefObject<HTMLParagraphElement | null>
	consent: Consent
	allowed: boolean
	onDone: () => void
}) {
	const { t } = useTranslation('common')
	const locale = useLocale()
	const fetcher = useFetcher()

	// The recorded choice describes what was persisted (server value); the toggle
	// reflects the live gate so it flips optimistically the moment it is used.
	const date = consent.at ? formatDate(locale, new Date(consent.at)) : ''
	const recorded =
		consent.state === 'accepted'
			? t('consent.settings.stateAllowed', { date })
			: consent.state === 'declined'
				? t('consent.settings.stateDeclined', { date })
				: t('consent.settings.stateUnset')

	return (
		<div
			role="status"
			aria-label={t('consent.settings.label')}
			className={`${cardBase} sm:w-96`}
		>
			<p ref={titleRef} tabIndex={-1} className="text-body-sm font-semibold outline-none">
				{t('consent.settings.title')}
			</p>
			<div className="border-border flex items-center justify-between gap-3 rounded-md border p-3">
				<div className="flex flex-col gap-0.5">
					<span className="text-body-xs font-medium">
						{t('consent.settings.rowLabel')}
					</span>
					<span className="text-body-2xs text-muted-foreground">{recorded}</span>
				</div>
				<Switch
					aria-label={t('consent.settings.toggleLabel')}
					checked={allowed}
					onCheckedChange={(checked) =>
						fetcher.submit(
							{ intent: checked ? 'accept' : 'decline' },
							{ method: 'POST', action: '/resources/consent' },
						)
					}
				/>
			</div>
			<div className="flex justify-end">
				<Button type="button" size="sm" variant="outline" onClick={onDone}>
					{t('consent.settings.done')}
				</Button>
			</div>
		</div>
	)
}
